'use client'

import { useEffect, useRef, type ReactNode } from 'react'

/**
 * Minimal, dependency-free scroll reveal for the above-footer card fan. The cards render in their
 * FINAL fanned position on the server (crawlable, correct with JS disabled). On mount this arms the
 * collapsed state (`data-fan-armed`), then plays the fan-out once the cluster scrolls into view
 * (`data-fan-inview`). Reduced-motion users skip straight to the final state. The section lives at
 * the very bottom of the page (below the fold), so the arming collapse happens off-screen — no flash.
 * The actual transition lives in globals.css (`[data-fan-*]` rules) to keep this island tiny.
 */
export function FanReveal({ children, className }: { children: ReactNode; className?: string }) {
    const ref = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const el = ref.current
        if (!el) return

        // Reduced motion: reveal immediately, never arm the collapse.
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            el.setAttribute('data-fan-inview', '')
            return
        }

        el.setAttribute('data-fan-armed', '')

        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        el.setAttribute('data-fan-inview', '')
                        observer.disconnect()
                        break
                    }
                }
            },
            { threshold: 0.2, rootMargin: '0px 0px -10% 0px' }
        )
        observer.observe(el)

        return () => observer.disconnect()
    }, [])

    return (
        <div ref={ref} data-fan-root className={className}>
            {children}
        </div>
    )
}
